import {
    useAppInsightsContext,
    useTrackEvent,
} from '@microsoft/applicationinsights-react-js';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { DateAdapter } from '@myphx-engineering/date-utils/client';
import { useForm } from '@myphx-engineering/phx-design-system/hooks';

import type { CreateAccountFormModel } from '../models/form.models';

export const useSignupFormFieldProps = (
    form: ReturnType<typeof useForm<CreateAccountFormModel>>
) => {
    const { t } = useTranslation('pds');
    const aiCtx = useAppInsightsContext();

    const formValuesRef = useRef(form.values);
    formValuesRef.current = form.values;

    const trackDOB =
        // oxlint-disable-next-line react-hooks/rules-of-hooks -- tracked in docs/oxlint-violations.md
        aiCtx && useTrackEvent(aiCtx, 'SIGNUP_DATEOFBIRTH_CLICK', {});

    const trackFullName = useTrackEvent(aiCtx, 'SIGNUP_FULLNAME_CLICK', {});

    const firstNameProps = form.getInputProps('firstName');
    firstNameProps.onFocus = () => {
        trackFullName({
            message: ' First name is clicked',
        });
        form.getInputProps('firstName').onFocus();
    };

    const lastNameProps = form.getInputProps('lastName');
    lastNameProps.onFocus = () => {
        trackFullName({
            message: ' Last name is clicked',
        });
        form.getInputProps('lastName').onFocus();
    };

    // These errors get aggregated under one "name" error
    delete firstNameProps.error;
    delete lastNameProps.error;

    const onGroupValidation = (error: string | null) => {
        if (error) {
            form.setFieldError('dobDate', error);
            form.setFieldError('dobMonth', true);
            form.setFieldError('dobDay', true);
            form.setFieldError('dobYear', true);
            return;
        }

        const { dobMonth, dobDay, dobYear } = formValuesRef.current;
        const month = Number.parseInt(dobMonth, 10);
        const day = Number.parseInt(dobDay, 10);
        const year = Number.parseInt(dobYear, 10);

        if (!DateAdapter.validateAge(year, month, day, 13)) {
            form.setFieldError('dobDate', t('common.formErrors.dob.tooYoung'));
            form.setFieldError('dobMonth', true);
            form.setFieldError('dobDay', true);
            form.setFieldError('dobYear', true);
            return;
        }

        form.clearFieldError('dobDate');
        form.clearFieldError('dobMonth');
        form.clearFieldError('dobDay');
        form.clearFieldError('dobYear');
    };

    const dobMonthProps = form.getInputProps('dobMonth');
    const originalDobMonthOnFocus = dobMonthProps.onFocus;
    dobMonthProps.onFocus = () => {
        trackDOB({ message: 'DOB month field is clicked' });
        originalDobMonthOnFocus?.();
    };

    const dobDayProps = form.getInputProps('dobDay');
    const originalDobDayOnFocus = dobDayProps.onFocus;
    dobDayProps.onFocus = () => {
        trackDOB({ message: 'DOB day field is clicked' });
        originalDobDayOnFocus?.();
    };

    const dobYearProps = form.getInputProps('dobYear');
    const originalDobYearOnFocus = dobYearProps.onFocus;
    dobYearProps.onFocus = () => {
        trackDOB({ message: 'DOB year field is clicked' });
        originalDobYearOnFocus?.();
    };

    return {
        firstNameProps,
        lastNameProps,
        dobMonthProps,
        dobDayProps,
        dobYearProps,
        onGroupValidation,
    };
};
